import {useTranslation} from '@aboutyou/i18n';
import FuzzySearch from 'fuzzy-search';
import {useMemo} from 'react';
import accents from 'remove-accents';
import {GenericFilterItemGroup} from './GenericFilteredItems/types';

const useFilteredGroups = ({
  itemGroups,
  search,
}: {
  itemGroups?: Array<GenericFilterItemGroup>;
  search: string;
}) => {
  return useMemo(() => {
    if (!itemGroups?.length) return [];
    if (search === '') return itemGroups;

    return itemGroups.reduce((filteredGroups, itemGroup) => {
      const {items, groupLabel} = itemGroup;

      const filteredItems = items.filter(item => {
        const label = item.label.toLocaleLowerCase();
        const labelWithoutAccents = accents.remove(label);

        const searcher = new FuzzySearch([label, labelWithoutAccents], [], {
          caseSensitive: false,
          sort: true,
        });
        const foundResultInFuzzySearch =
          searcher.search(search.toLowerCase()).length > 0;

        return (
          label.includes(search.toLowerCase()) ||
          labelWithoutAccents.includes(search.toLowerCase()) ||
          foundResultInFuzzySearch
        );
      });

      return filteredItems.length === 0
        ? filteredGroups
        : [
            ...filteredGroups,
            {
              items: filteredItems,
              groupLabel,
            },
          ];
    }, [] as Array<GenericFilterItemGroup>);
  }, [itemGroups, search]);
};

export const useSelectedGroups = ({
  itemGroups,
  activeIds,
  search,
}: {
  itemGroups: Array<GenericFilterItemGroup>;
  activeIds: Array<string>;
  search: string;
}) => {
  const {t} = useTranslation();
  const selectedGroupLabel = t('Selected');
  const filteredGroups = useFilteredGroups({itemGroups, search});

  return useMemo(() => {
    if (!filteredGroups?.length) return null;

    const selectedItems = filteredGroups
      .flatMap(itemGroup => itemGroup.items)
      .filter(
        (item, index, self) =>
          activeIds.includes(item.id) &&
          index === self.findIndex(b => b.id === item.id),
      );

    return selectedItems.length
      ? ({
          items: selectedItems,
          groupLabel: selectedGroupLabel,
          dataTestId: 'SectionSelected',
        } as GenericFilterItemGroup)
      : null;
  }, [filteredGroups, activeIds, selectedGroupLabel]);
};
